// useEditablePersonnes.js
import { useMemo, useState } from "react";

/** Nettoie un nom/prénom (trim) et ignore les chaînes vides */
const sanitizeName = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
};

/**
 * Hook d'édition inline pour la liste des personnes.
 * - personnes: [{id, nom, prenom, dateNaissance, ...}]
 * - onSubmit: async (payload) => { ... } // payload: { personnes: [{id, nom?, prenom?}, ...] }
 */
export function useEditablePersonnes(personnes = [], onSubmit = async () => {}) {
  // drafts: { [id]: { nom, prenom } } – valeurs en cours d'édition
  const [drafts, setDrafts] = useState({});
  // ids en édition
  const [editing, setEditing] = useState(new Set());

  // helpers pour retrouver une personne d'origine
  const byId = useMemo(() => {
    const m = new Map();
    personnes.forEach((p) => m.set(String(p.id), p));
    return m;
  }, [personnes]);

  const startEdit = (id) => {
    const key = String(id);
    if (!byId.has(key)) return;
    setEditing((prev) => new Set([...prev, key]));
    setDrafts((prev) => {
      if (prev[key]) return prev; // déjà présent
      const p = byId.get(key);
      return { ...prev, [key]: { nom: p.nom ?? "", prenom: p.prenom ?? "" } };
    });
  };

  const stopEdit = (id) => {
    const key = String(id);
    setEditing((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setDrafts((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const stopAll = () => {
    setEditing(new Set());
    setDrafts({});
  };

  const changeField = (id, field, value) => {
    const key = String(id);
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? {}), [field]: value },
    }));
  };

  /** Construit le payload minimal (seulement les champs changés et valides) */
  const buildPayload = () => {
    const updates = [];

    Object.entries(drafts).forEach(([key, draft]) => {
      const original = byId.get(key);
      if (!original) return;

      const nom = sanitizeName(draft.nom);
      const prenom = sanitizeName(draft.prenom);

      const changed = {};
      if (nom !== null && nom !== original.nom) changed.nom = nom;
      if (prenom !== null && prenom !== original.prenom) changed.prenom = prenom;

      if (Object.keys(changed).length > 0) {
        // essaie de renvoyer id en number si possible
        const idNum = Number.isFinite(+key) ? +key : key;
        updates.push({ id: idNum, ...changed });
      }
    });

    return updates.length ? { personnes: updates } : null;
  };

  const hasAnyEdit = useMemo(() => editing.size > 0, [editing]);
  const hasChanges = useMemo(() => buildPayload() !== null, [drafts, byId]);

  /** Valide : appelle onSubmit(payload) si changements, puis quitte le mode édition */
  const validate = async () => {
    const payload = buildPayload();
    if (!payload) return { ok: false, reason: "no_changes" };
    await onSubmit(payload);
    stopAll();
    return { ok: true };
  };

  return {
    drafts,
    editing,
    hasAnyEdit,
    hasChanges,
    startEdit,
    stopEdit,
    stopAll,
    changeField,
    buildPayload,
    validate,
  };
}
